import { createSimpleEvent, type SimpleEvent } from '@passionware/simple-event';
import { type ReactNode, useMemo, useState } from 'react';
import type { AnySpec, KindMap } from 'v5/core/form-schema/formKinds.types';
import type { ListUnit } from 'v5/core/form-schema/formSchema.types';
import { rowIdUtils } from 'v5/core/form-schema/formRows';
import { ListEntriesUnitView } from './ListEntriesUnitView';
import { hasListLines, kindListRow } from './ListLinesField';
import { ListRowsField } from './ListRowsField';
import {
  fromListFormRows,
  type ListFormRow,
  readRowValues,
  toListFormRows
} from './listFormRows';
import type {
  FormKit,
  ListUnitBinding,
  SchemaFormSize,
  SchemaFormVariant,
  UnitFoldSource,
  UnitTarget
} from './schemaForm.types';
import { useFieldsResolver } from './fieldValidation';
import { SchemaUnitEditor } from './SchemaUnitEditor';
import { fieldPath } from './unitPath';

export interface ListUnitViewProps<M extends KindMap<M>> {
  unit: ListUnit<AnySpec<M>>;
  binding: ListUnitBinding<M>;
  kit: FormKit<M>;
  variant: SchemaFormVariant;
  size: SchemaFormSize;
  foldSource: UnitFoldSource;
  /** See `SchemaSectionsProps.editUnitSignal`. */
  editUnitSignal?: SimpleEvent<UnitTarget>;
  /** See `UnitEditorProps.icon`. */
  icon: ReactNode;
}

/** The unit's form: the rows under one key. */
type ListFormValues<M extends KindMap<M>> = { items: ListFormRow<M>[] };

/**
 * A list unit. Rows of one field that fits a line are edited as a whole
 * (`ListLinesUnitView`); bigger entries (rates, field groups with several
 * fields) each on their own (`ListEntriesUnitView`).
 */
export function ListUnitView<M extends KindMap<M>>(
  props: ListUnitViewProps<M>
) {
  return hasListLines(props.kit, props.unit.item.fields) ? (
    <ListLinesUnitView {...props} />
  ) : (
    <ListEntriesUnitView {...props} />
  );
}

/**
 * A list edited as a whole, like any unit: at rest it reads as text; the
 * edit button (or a double click) turns every row into a line to edit
 * (`ListRowsField`). Adding, removing, reordering and editing rows stay in
 * the unit's form until Save, which writes the list once; Cancel drops them.
 */
function ListLinesUnitView<M extends KindMap<M>>({
  unit,
  binding,
  kit,
  variant,
  size,
  foldSource,
  editUnitSignal,
  icon
}: ListUnitViewProps<M>) {
  const fold = foldSource.useFold(unit);
  const resolver = useFieldsResolver<M, ListFormValues<M>>(
    data =>
      data.items.flatMap((row, index) => {
        const values = readRowValues(unit.item.fields, row.values);
        return unit.item.fields.map(field => ({
          path: `items.${index}.values.${fieldPath('', field.key)}`,
          spec: field,
          values
        }));
      }),
    kit
  );
  const [addSignal] = useState(() => createSimpleEvent<string | null>());
  const [field] = unit.item.fields;
  const startsFromTyped =
    field !== undefined && kindListRow(kit, field)?.fromTyped !== undefined;
  const savedRowKeys = useMemo(
    () => new Set(binding.items.map(item => rowIdUtils.key(item.rowId))),
    [binding.items]
  );
  return (
    <SchemaUnitEditor<ListFormValues<M>>
      unit={unit}
      formValues={{ items: toListFormRows(binding.items) }}
      resolver={resolver}
      onSave={data =>
        binding.saveItems(
          fromListFormRows(unit.item.fields, data.items, binding.items)
        )
      }
      content={
        <ListRowsField
          unit={unit}
          name="items"
          kit={kit}
          newItem={binding.newItem}
          usedAddOptions={binding.usedAddOptions}
          savedRowKeys={savedRowKeys}
          addSignal={addSignal}
        />
      }
      icon={icon}
      required={false}
      groupTitle={false}
      isEmpty={data => data.items.length === 0}
      variant={variant}
      size={size}
      editUnitSignal={editUnitSignal}
      fold={
        fold === null
          ? undefined
          : { ...fold, summary: String(binding.items.length) }
      }
      add={{
        label: unit.add.label,
        editHidden: binding.items.length === 0,
        ...(unit.add.type === 'single-or-template' && {
          menu: {
            blank: unit.add.blank,
            templates: unit.add.templates.options,
            manage: unit.add.manage,
            usedTemplates: binding.usedAddOptions(
              binding.items.map(item => item.values)
            )
          }
        }),
        // A line that starts from what is typed: the “+” just goes to the
        // empty line (`UNIT_ADD_ATTRIBUTE`), adding no blank row above it.
        ...(startsFromTyped
          ? {}
          : {
              onAdd: (templateId: string | null) => addSignal.emit(templateId)
            })
      }}
    />
  );
}
